import { Box, Button, Flex, Group, Text } from '@mantine/core';
import { useNavigation, useOne, useParsed } from '@refinedev/core';
import { IconChevronLeft } from '@tabler/icons-react';
import {
  camelCaseOrSnakeCaseToWords,
  fieldsToHide,
} from '../data-capture/merge-review/ReviewHelperFunctions';
import { useDeleteEntities } from '../data-capture/merge-review/useDeleteEntities';
import { UpsertEntityInput, useUpsertEntity } from '../data-capture/merge-review/useUpsertEntities';
import dataCaptureClasses from '../data-capture/DataCapture.module.css';
import { GenericErrorPage } from '../error/GenericErrorPage';
import { EntityForm } from '../UI/dynamicUI/EntityForm';
import PageLoader from '../UI/loader/PageLoader';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { EntityWithFieldMetadata } from 'src/UI/dynamicUI/types';

type Params = {
  entityId: string;
  entityType: 'Account' | 'Asset' | 'Liability' | 'Policy';
};

export default function ViewEntityPage() {
  const { params } = useParsed<Params>();
  const { goBack } = useNavigation();
  const {
    upsertEntities,
    isLoading: isUpsertingEntity,
    isError: upsertingEntityFailed,
  } = useUpsertEntity();
  const { clientId } = useCurrentClient();

  const {
    deleteEntity,
    isLoading: isDeletingEntities,
    isError: deleteEntitiesFailed,
  } = useDeleteEntities();

  const handleUpdate = async (values: UpsertEntityInput[]) => {
    if (!clientId) {
      return;
    }
    await upsertEntities(clientId, values, true);
    await refetch();
    goBack();
  };

  const { data, isLoading, isError, refetch } = useOne<EntityWithFieldMetadata>({
    resource: `clients/${clientId}/${params?.entityType}/entities`,
    id: params?.entityId,
    queryOptions: {
      enabled: !!params?.entityId && !!params?.entityType,
    },
  });

  const handleDelete = async () => {
    if (!params?.entityId || !data?.data) {
      return;
    }
    await deleteEntity(data?.data, clientId);
    goBack();
  };

  if (isLoading || isUpsertingEntity || isDeletingEntities) {
    return <PageLoader absolute={false} />;
  }

  if (isError || upsertingEntityFailed || deleteEntitiesFailed) {
    return <GenericErrorPage error={{ isError, upsertingEntityFailed, deleteEntitiesFailed }} />;
  }

  return data?.data ? (
    <Box>
      <Flex direction={'row'} align={'center'}>
        <IconChevronLeft className={dataCaptureClasses.pointer} onClick={goBack} />
        <Text fw={'600'} size={'lg'} ml={'sm'}>
          {camelCaseOrSnakeCaseToWords(data.data.entityType)}
        </Text>
      </Flex>

      <Box ml={'xl'} pt={'xl'} maw={320}>
        <EntityForm
          entity={data.data}
          fieldsToHide={fieldsToHide}
          footer={
            <Group>
              <Button size={'xs'} type={'submit'} mt={'xxl'}>
                Update
              </Button>
              <Button variant={'default'} size={'xs'} mt={'xxl'} onClick={handleDelete}>
                Delete
              </Button>
            </Group>
          }
          filterUntouchedFields={false}
          onSubmit={values => handleUpdate(values)}
        />
      </Box>
    </Box>
  ) : null;
}
